// src/components/ProfileView.tsx

/*
マイページの処理
[主な機能]
・ユーザー情報の表示と編集
・広告の表示
・法務情報の表示
・ログアウト
・ページの切り替え
・メッセージの表示 
*/
import React, { useContext } from 'react';
import { ChevronLeft, LogOut } from 'lucide-react';
import ProfileEditSection from './ProfileEditSection';
import AdsSection from '../ads/AdsSection';
import { useUserProfile } from '../../hooks/useUserProfile';
import { UserContext } from '../../services/user/LoginUserContext';
import { logger } from '../../utils/logger';
import { Profile } from '../../types';

import MyQuizzesShareList from '../quiz/MyQuizzesShareList';

// Propsのインターフェースを定義
interface ProfileViewProps {
  userId: string;
  handlePageChange: (page: string) => void;
  showMessage: (message: string, onConfirm: () => void, onCancel: null | (() => void), showCancel: boolean) => void;
}


const ProfileView: React.FC<ProfileViewProps> = ({ userId, handlePageChange, showMessage }) => {

  const { logout } = useContext(UserContext);

  // useUserProfileフックの戻り値の型を明示
  const { profile, isLoading, error, }: { profile: Profile | null; isLoading: boolean; error: Error | null } = useUserProfile(userId);


  const handleLogoutClick = async () => {
    try {
      await logout();
      showMessage('ログアウトしました。', () => { }, null, false);
    } catch (error) {
      logger.error("ログアウト中にエラーが発生しました:", error);
      showMessage('ログアウトに失敗しました。', () => { /* OKボタンが押されたときの処理 */ }, null, false);
    }
  };

  if (isLoading) {
    return <div className="text-center mt-10">読み込み中...</div>;
  }

  if (error) {
    return <div className="text-center mt-10 text-red-500">プロフィールの取得に失敗しました。</div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => profile?.nickname && handlePageChange('home')}
          className="p-2 rounded-full hover:bg-gray-200 disabled:opacity-50"
          // profileがnullの場合、profile.nicknameにアクセスしないようにオプショナルチェイニングを使用
          // また、disabled属性にprofileがnullの場合を考慮してオプショナルチェイニングを追加
          disabled={!profile?.nickname}
        >
          <ChevronLeft size={28} />
        </button>
        <h1 className="text-3xl font-bold text-gray-800">マイページ</h1>
        <button
          onClick={handleLogoutClick}
          className="flex items-center py-2 px-5 bg-red-500 text-white font-semibold rounded-lg shadow hover:bg-red-600 transition"
        >
          <LogOut size={20} className="mr-2" /> ログアウト
        </button>
      </div>

      {/* プロフィール編集セクション */}
      <ProfileEditSection
        userId={userId}
        handlePageChange={handlePageChange}
        showMessage={showMessage}
      />

      <div className=" p-6 mb-2">
      {/* 広告セクション */}
        <AdsSection userId={userId} />
      </div>

      <div className="p-6 mb-2">
        {/* その他のプロフィール情報 */}
        <MyQuizzesShareList userId={userId} />
      </div>


      {/* 法務情報 */}
      <div className="bg-white p-6 rounded-2xl shadow-xl mb-8">
        <h2 className="text-2xl font-bold text-gray-700 mb-4">法務情報</h2>
        <div className="space-y-2">
          <a
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sky-600 hover:underline"
          >
            利用規約
          </a>
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sky-600 hover:underline"
          >
            プライバシーポリシー
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;